import { useLayoutEffect, useRef, useState } from "react";

import { keyframes, styled } from "styled-components";
import rearBushSprite from "../../assets/splash/BackPlants.png";
import logoSprite from "../../assets/splash/Mines.png";
import porcupineASprite from "../../assets/splash/Porcupine01.png";
import porcupineBSprite from "../../assets/splash/Porcupine02.png";
import porcupineCSprite from "../../assets/splash/Porcupine03.png";
import sparksSprite from "../../assets/splash/Sparks.png";
import backgroundSprite from "../../assets/splash/SplashScreenBkg.png";

export const ScaleIn = keyframes`
    0% { transform: scale(0) }
    100% { transform: scale(1) }
`;

export const StarsAnimation = keyframes`
    0% { opacity: 0 }
    100% { opacity: 1 }
`;

export const PorcupineDropA = keyframes`
    0% { transform: translateY(-1000px) }
    70% { transform: translateY(50px) }
    100% { transform: translateY(0px) }
`;

export const PorcupineDropB = keyframes`
    0% { transform: translate(130px, -1000px) }
    70% { transform: translate(130px, 125px) }
    100% { transform: translate(130px, 75px) }
`;

export const PorcupineDropC = keyframes`
    0% { transform: translate(-90px, -1000px) }
    70% { transform: translate(-90px, 100px) }
    100% { transform: translate(-90px, 50px) }
`;

export const BushIntro = keyframes`
    0% { transform: translateY(100px); opacity: 0 }
    100% { transform: translateY(0px); opacity: 1 }
`;

const StyledSplash = styled.div`
    background-image: url(${backgroundSprite});
    background-size: cover;
    background-position: center;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Sprite = styled.div`
    background-size: contain;
    background-repeat: no-repeat;
    aspect-ratio: 1;
`;

const Container = styled.div<{$scale: number}>`
    position: relative;
    width: 100%;
    height: 100%;
    transform: scale(${ props => props.$scale});
`;

const Logo = styled(Sprite)`
    background-image: url(${logoSprite});
    width: 100%;
    max-width: 500px;
    position: absolute;
    animation: ${ScaleIn} 500ms cubic-bezier(.81,1.02,.45,1.66);
    animation-delay: 1200ms;
    transform: scale(0);
    
    animation-fill-mode: forwards;
`;

const LogoContainer = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    transform: translateY(160px);
`;

const SparksContainer = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    transform: translateY(-50px);
`;

const RearBushContainer = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    transform: translateY(-130px);
`;

const PorcupineContainer = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    transform: translateY(-120px) scale(1.2);
`;

const PorcupineA = styled(Sprite)`
    background-image: url(${porcupineASprite});
    width: 216px;
    height: 258px;
    position: absolute;
    transform: translate(0px) translateY(-1000px);
    animation: ${PorcupineDropA} 500ms ease-in;
    animation-delay: 1400ms;
    animation-fill-mode: forwards;
`;

const PorcupineB = styled(Sprite)`
    background-image: url(${porcupineBSprite});
    width: 157px;
    height: 196px;
    position: absolute;
    
    transform: translate(130px) translateY(-1000px);
    animation: ${PorcupineDropB} 500ms ease-in;
    animation-delay: 1500ms;
    animation-fill-mode: forwards;
`;

const PorcupineC = styled(Sprite)`
    background-image: url(${porcupineCSprite});
    width: 190px;
    height: 200px;
    position: absolute;
    
    transform: translate(-130px) translateY(-1000px);
    animation: ${PorcupineDropC} 500ms ease-in;
    animation-delay: 1700ms;
    animation-fill-mode: forwards;
`;

const RearBush = styled(Sprite)`
    background-image: url(${rearBushSprite});
    width: 497px;
    height: 227px;
    animation: ${BushIntro} 500ms ease-out;
    animation-delay: 2000ms;
    animation-fill-mode: forwards;
    opacity: 0;
`;

const SparksA = styled(Sprite)`
    background-image: url(${sparksSprite});
    width: 208px;
    height: 275px;
    position: absolute;
    animation: ${StarsAnimation} 500ms;
    animation-delay: 2000ms;
    animation-fill-mode: forwards;
    opacity: 0;
    transform: translateX(-250px);
`;

const SparksB = styled(Sprite)`
    background-image: url(${sparksSprite});
    width: 208px;
    height: 275px;
    position: absolute;
    animation: ${StarsAnimation} 1000ms;
    animation-delay: 2300ms;
    animation-fill-mode: forwards;
    opacity: 0;
    transform: translateX(250px) scaleX(-1);
`;

const Splash: React.FC<SplashProps> = ({

    ...props
}) => {
    
    const [scale, setScale] = useState(1);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        
        function resize() {
            if (wrapperRef.current) {
                const verticalRatio = Math.min(((window.screen.availHeight - 270)) / (wrapperRef.current.clientHeight / scale), 1);
                const horizontalRatio = Math.min((window.innerWidth - 200) / (wrapperRef.current.clientWidth / scale), 1);
                if (verticalRatio < horizontalRatio) {
                    setScale(verticalRatio);
                } else {
                    setScale(horizontalRatio);
                }
            }
        }
        setTimeout(() => {
            resize();
        }, 1000)
        
        window.addEventListener("resize", resize);
    }, []);

    return (
        <StyledSplash>
            <Container ref={wrapperRef} $scale={scale}>
                <RearBushContainer>
                    <RearBush />
                </RearBushContainer>
                <PorcupineContainer>
                    <PorcupineA />
                    <PorcupineB />
                    <PorcupineC />
                </PorcupineContainer>
                <LogoContainer>
                    <Logo />
                </LogoContainer>
                <SparksContainer>
                    <SparksA />
                    <SparksB />
                </SparksContainer>
                
            </Container>
        </StyledSplash>
    )
};

interface SplashProps {
    
}

export default Splash;